import {Checkbox, ListItemText, MenuItem, TextField} from "@mui/material";
import * as React from "react";
import {useState} from "react";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {addFilter, DECIMAL_REG_EXP, getFilterValue} from "../../../utils/FilterUtils";
import {
    ID_FILTER_PRINTOUT_CLUSTER,
    ID_FILTER_PRINTOUT_CODE_SAP,
    ID_FILTER_PRINTOUT_CODE_SAP_M,
    ID_FILTER_PRINTOUT_EMAIL_M, ID_FILTER_PRINTOUT_INVOICE_NUMBER,
    ID_FILTER_PRINTOUT_MSNAME,
    ID_FILTER_PRINTOUT_NAME_M,
    ID_FILTER_PRINTOUT_PROPERTY,
    ID_FILTER_PRINTOUT_PUBLISHEDDATEFROM,
    ID_FILTER_PRINTOUT_PUBLISHEDDATETO, ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER,
    ID_FILTER_PRINTOUT_STATUS,
    ID_FILTER_PRINTOUT_TOTAL_FROM,
    ID_FILTER_PRINTOUT_TOTAL_TO,
    ID_FILTER_PRINTOUT_TYPE,
    ID_FILTER_SEARCH,
    msSinglePrintoutDefaultValues
} from "../constants/MobilityStoreFilters";
import ColouredDot from "../../../component/ColouredDot";
import {
    ID_ACCESSORIES,
    ID_BE_FREE_EVO, ID_CAR_BOX,
    ID_CAR_CLOUD,
    ID_COMMISSIONS,
    ID_DAMAGE, ID_DAMAGE_SHEET, ID_FLEX_RENT,
    ID_FUEL,
    ID_TRANSFERS,
    ID_VOUCHERS
} from "../constants/PrintoutTypes";
import {
    ID_BILLED,
    ID_ERROR,
    ID_PROCESSING,
    ID_PUBLISHED,
    ID_SENT,
    ID_TO_SEND
} from "../constants/MobilityStoreStatuses";
import {COLOR_BILLED, COLOR_ERROR, COLOR_PUBLISHED, COLOR_SENT, COLOR_TO_SEND} from "../constants/MobilityStoreColors";
import {STATE_SINGLE_PRINTOUT_ACTIVE_FILTERS} from "../constants/MobilityStoreRecoil";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {ID_LICENSEES, ID_MANDATORY} from "../../cluster/constants/ClusterMSTypes";

function SinglePrintoutsFilter() {

    const { t } = useTranslation('finance', { keyPrefix: 'mobility-store' });
    const filterDefs = useRecoilValue(STATE_SINGLE_PRINTOUT_ACTIVE_FILTERS);
    const setFilters = useSetRecoilState(STATE_SINGLE_PRINTOUT_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");

    const schema = Yup.object().shape({
        msName: Yup.string().nullable(),
        msSapId: Yup.string().nullable(),
        msVendorId: Yup.string().nullable(),
        msVendorName: Yup.string().nullable(),
        msVendorEmail: Yup.string().nullable(),
        msType: Yup.string().nullable(),
        type: Yup.string(),
        clusterName: Yup.string().nullable(),
        totalAmountFrom: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('filters.errors.decimal-numbers')
        }).test("totalAmountTo", t('filters.errors.invalid-number'), (value, context) => {
            return (!value || !context.parent.totalTo || value <= context.parent.totalAmountTo)
        }),
        totalAmountTo: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('filters.errors.decimal-numbers')
        }).test("testTotalTo", t('filters.errors.invalid-number'), (value, context) => {
            return (!value || !context.parent.totalAmountFrom || value >= context.parent.totalAmountFrom)
        }),
        sentDateFrom: Yup.date().nullable().test("testDateFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateTo || (value <= context.parent.sentDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        sentDateTo: Yup.date().nullable().test("testDateTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.sentDateFrom || (value >= context.parent.sentDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        status: Yup.array()
    });

    const defaultValues = {
        msName: getFilterValue(ID_FILTER_PRINTOUT_MSNAME, filterDefs) || "",
        msSapId: getFilterValue(ID_FILTER_PRINTOUT_CODE_SAP, filterDefs) || "",
        msVendorId: getFilterValue(ID_FILTER_PRINTOUT_CODE_SAP_M, filterDefs) || "",
        msVendorName: getFilterValue(ID_FILTER_PRINTOUT_NAME_M, filterDefs) || "",
        msVendorEmail: getFilterValue(ID_FILTER_PRINTOUT_EMAIL_M, filterDefs) || "",
        msType: getFilterValue(ID_FILTER_PRINTOUT_PROPERTY, filterDefs) || "",
        type: getFilterValue(ID_FILTER_PRINTOUT_TYPE, filterDefs) || "",
        clusterName: getFilterValue(ID_FILTER_PRINTOUT_CLUSTER, filterDefs) || "",
        totalAmountFrom: getFilterValue(ID_FILTER_PRINTOUT_TOTAL_FROM, filterDefs) || "",
        totalAmountTo: getFilterValue(ID_FILTER_PRINTOUT_TOTAL_TO, filterDefs) || "",
        sentDateFrom: getFilterValue(ID_FILTER_PRINTOUT_PUBLISHEDDATEFROM, filterDefs) || null,
        sentDateTo: getFilterValue(ID_FILTER_PRINTOUT_PUBLISHEDDATETO, filterDefs) || null,
        status: getFilterValue(ID_FILTER_PRINTOUT_STATUS, filterDefs) || [],
        invoiceNumber: getFilterValue(ID_FILTER_PRINTOUT_INVOICE_NUMBER, filterDefs) || "",
        sapInvoiceNumber: getFilterValue(ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER, filterDefs) || ""
    };

    const renderMSName = (field: any) => (
        <TextField
            fullWidth
            name="name"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_MSNAME)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderMSCodeSap = (field: any) => (
        <TextField
            fullWidth
            name="code-sap"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_CODE_SAP)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderMSCodeSapM = (field: any) => (
        <TextField
            fullWidth
            name="code-sap-m"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_CODE_SAP_M)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderMSNameM = (field: any) => (
        <TextField
            fullWidth
            name="name-m"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_NAME_M)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderMSEmailM = (field: any) => (
        <TextField
            fullWidth
            name="email-m"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_EMAIL_M)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderMSProperty = (field: any) => (
        <TextField fullWidth
                   select
                   label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_PROPERTY)}
                   value={field.value}
                   onChange={field.onChange}
        >
            <MenuItem value={""}>
                <em/>
            </MenuItem>
            {cmsTypes.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );

    const renderType = (field: any) => (
            <TextField fullWidth
                       select
                       label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_TYPE)}
                       value={field.value}
                       onChange={field.onChange}
            >
                <MenuItem value={""}>
                    <em/>
                </MenuItem>
                {types.map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderCluster = (field: any) => (
        <TextField
            fullWidth
            name="cluster"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_CLUSTER)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderTotalFrom = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            name={"totalFrom"}
            label={t("filters.single-printouts." + ID_FILTER_PRINTOUT_TOTAL_FROM)}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    );

    const renderTotalTo = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            name={"totalTo"}
            label={t("filters.single-printouts." + ID_FILTER_PRINTOUT_TOTAL_TO)}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    );

    const renderPublishedDateFrom = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.single-printouts.published-date-from')}
                    views={['day']}
                    maxDate={values['publishedDateTo'] !== null ? values['publishedDateTo'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"publishedDateFrom"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    );

    const renderPublishedDateTo = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.single-printouts.published-date-to')}
                    views={['day']}
                    minDate={values['publishedDateFrom'] !== null ? values['publishedDateFrom'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"publishedDateTo"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    );

    const renderStatus = (field: any) => (
            <TextField fullWidth
                       label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_STATUS)}
                       select
                       SelectProps={{
                           multiple: true,
                           renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                       }}
                       value={field.value}
                       onChange={field.onChange}
            >
                {statuses.map((status) =>
                        <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                            <ColouredDot color={status?.color}/>
                            <ListItemText primary={status.label}/>
                        </MenuItem>
                )}
            </TextField>
    );

    const renderInvoiceNumber = (field: any) => (
        <TextField
            fullWidth
            name="invoice-number"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_INVOICE_NUMBER)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const renderSapInvoiceNumber = (field: any) => (
        <TextField
            fullWidth
            name="sap-invoice-number"
            label={t('filters.single-printouts.' + ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER)}
            value={field.value}
            onChange={field.onChange}
        ></TextField>
    );

    const fields = {
        msName: renderMSName,
        msSapId: renderMSCodeSap,
        msVendorId: renderMSCodeSapM,
        msVendorName: renderMSNameM,
        msVendorEmail: renderMSEmailM,
        msType: renderMSProperty,
        type: renderType,
        clusterName: renderCluster,
        totalAmountFrom: renderTotalFrom,
        totalAmountTo: renderTotalTo,
        sentDateFrom: renderPublishedDateFrom,
        sentDateTo: renderPublishedDateTo,
        status: renderStatus,
        invoiceNumber: renderInvoiceNumber,
        sapInvoiceNumber: renderSapInvoiceNumber
    }

    const cmsTypes = [
        {
            value: ID_MANDATORY,
            label: t('printout-ms-types.' + ID_MANDATORY)
        },
        {
            value: ID_LICENSEES,
            label: t('printout-ms-types.' + ID_LICENSEES)
        }
    ];

    const types = [
        {
            value: ID_ACCESSORIES,
            label: t('printout-types.' + ID_ACCESSORIES)
        },
        {
            value: ID_FUEL,
            label: t('printout-types.' + ID_FUEL)
        },
        {
            value: ID_TRANSFERS,
            label: t('printout-types.' + ID_TRANSFERS)
        },
        {
            value: ID_COMMISSIONS,
            label: t('printout-types.' + ID_COMMISSIONS)
        },
        {
            value: ID_DAMAGE,
            label: t('printout-types.' + ID_DAMAGE)
        },
        {
            value: ID_VOUCHERS,
            label: t('printout-types.' + ID_VOUCHERS)
        },
        {
            value: ID_BE_FREE_EVO,
            label: t('printout-types.' + ID_BE_FREE_EVO)
        },
        {
            value: ID_CAR_CLOUD,
            label: t('printout-types.' + ID_CAR_CLOUD)
        },
        {
            value: ID_FLEX_RENT,
            label: t('printout-types.' + ID_FLEX_RENT)
        },
        {
            value: ID_CAR_BOX,
            label: t('printout-types.' + ID_CAR_BOX)
        },
        {
            value: ID_DAMAGE_SHEET,
            label: t('printout-types.' + ID_DAMAGE_SHEET)
        }
    ];

    const statuses = [
        {
            value: ID_PUBLISHED,
            label: t('statuses.' + ID_PUBLISHED),
            color: COLOR_PUBLISHED + '.transparent'
        },
        {
            value: ID_TO_SEND,
            label: t('statuses.' + ID_TO_SEND),
            color: COLOR_TO_SEND + '.transparent'
        },
        {
            value: ID_BILLED,
            label: t('statuses.' + ID_BILLED),
            color: COLOR_BILLED + '.transparent'
        },
        {
            value: ID_SENT,
            label: t('statuses.' + ID_SENT),
            color: COLOR_SENT + '.transparent'
        },
        {
            value: ID_PROCESSING,
            label: t('statuses.' + ID_PROCESSING),
        },
        {
            value: ID_ERROR,
            label: t('statuses.' + ID_ERROR),
            color: COLOR_ERROR + '.transparent'
        },
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_PRINTOUT_MSNAME, data.msName);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CODE_SAP, data.msSapId);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CODE_SAP_M, data.msVendorId);
        addFilter(setFilters, ID_FILTER_PRINTOUT_NAME_M, data.msVendorName);
        addFilter(setFilters, ID_FILTER_PRINTOUT_EMAIL_M, data.msVendorEmail);
        addFilter(setFilters, ID_FILTER_PRINTOUT_PROPERTY, data.msType);
        addFilter(setFilters, ID_FILTER_PRINTOUT_TYPE, data.type);
        addFilter(setFilters, ID_FILTER_PRINTOUT_CLUSTER, data.clusterName);
        addFilter(setFilters, ID_FILTER_PRINTOUT_TOTAL_FROM, data.totalAmountFrom);
        addFilter(setFilters, ID_FILTER_PRINTOUT_TOTAL_TO, data.totalAmountTo);
        addFilter(setFilters, ID_FILTER_PRINTOUT_PUBLISHEDDATEFROM, asStartOfDay(data.sentDateFrom));
        addFilter(setFilters, ID_FILTER_PRINTOUT_PUBLISHEDDATETO, asEndOfDay(data.sentDateTo));
        addFilter(setFilters, ID_FILTER_PRINTOUT_STATUS, data.status);
        addFilter(setFilters, ID_FILTER_PRINTOUT_INVOICE_NUMBER, data.invoiceNumber);
        addFilter(setFilters, ID_FILTER_PRINTOUT_SAP_INVOICE_NUMBER, data.sapInvoiceNumber);

    }

    const reset = () => {
        setFilters([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            searchInput={search} setSearchInput={setSearch}
                            customDefaultValues={msSinglePrintoutDefaultValues}
                            onSubmit={submit}
                            onReset={reset}
            />
    )
}

export default SinglePrintoutsFilter;